import { get, writable } from "svelte/store";
import type { AssetLoad } from "#lib/api/bundles.js";

export type Resolutions = Record<string, string>;

export interface IndexedAssets<T> {
	assets: Record<string, T>;
	resolutions: Resolutions;
}

export function indexAssets<T>(loaded: AssetLoad[], value: (asset: AssetLoad) => T): IndexedAssets<T> {
	const assets: Record<string, T> = {};
	const resolutions: Resolutions = {};
	const viaPassthrough: Set<string> = new Set();
	for (const asset of loaded) {
		const key = asset.key();
		const requested = asset.requestedKey();
		const resolved = !!requested && requested !== key;
		if (resolved) {
			resolutions[requested] = key;
		}
		if (viaPassthrough.has(key)) {
			continue;
		}
		assets[key] = value(asset);
		if (resolved) {
			viaPassthrough.add(key);
		}
	}
	return { assets, resolutions };
}

export function withResolution(current: Resolutions, asset: AssetLoad): Resolutions {
	const key = asset.key();
	const requested = asset.requestedKey();
	if (!requested || requested === key || current[requested] === key) {
		return current;
	}
	return { ...current, [requested]: key };
}

export function navigationTarget(resolutions: Resolutions, key: string): string {
	return resolutions[key] ?? key;
}

export interface ResolutionsStore {
	subscribe: ReturnType<typeof writable<{ gen: number; map: Resolutions }>>["subscribe"];
	replace: (map: Resolutions) => void;
	generation: () => number;
	merge: (gen: number, loaded: AssetLoad[]) => void;
}

export function createResolutionsStore(map: Resolutions = {}): ResolutionsStore {
	const store = writable({ gen: 0, map });
	return {
		subscribe: store.subscribe,
		replace: (next: Resolutions) => store.update(current => ({ gen: current.gen + 1, map: next ?? {} })),
		generation: () => get(store).gen,
		merge: (gen: number, loaded: AssetLoad[]) =>
			store.update(current => {
				if (current.gen !== gen) {
					return current;
				}
				let map = current.map;
				for (const asset of loaded) {
					map = withResolution(map, asset);
				}
				return map === current.map ? current : { gen: current.gen, map };
			}),
	};
}
